/* eslint-disable */
import { useState, useContext, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import moment from 'moment';
import MetricsCards from './techSessionCards';
import { AuthContext } from '../../../Contexts/AuthContext';
import { MODULE_NAMES } from '../../../Constants/modules';
import { useNavigate } from 'react-router-dom';
import { TECH_SESSION_PAGE } from '../../../Constants/strings';
import CommonCalendar from '../../../Components/Calendar/commonCalendar';
import UseAxios from '../../../Api/http/http';
import { getTechSessionData } from '../../../Api/request/techSessions';
import puzzleLoader from '../../../Assets/images/svg/loading puzzle.gif';  
const AddEditTechSession = lazy(() => import('../AddEditTechSession/addEditTechSession'));

// TechSessionCalendarView main component
const TechSessionCalendarView = () => {
  const { userProfile } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [techSessionData, setTechSessionData] = useState([]);
  const [techSessionMetrics, setTechSessionMetrics] = useState({});
  const [currentViewDate, setCurrentViewDate] = useState(new Date());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Memoize permission check
  const permission = useMemo(() => 
    userProfile?.permissions?.[MODULE_NAMES.TECH_SESSION_CALENDAR] || [],
    [userProfile?.permissions]
  );

  const hasAccess = useCallback((action) => {
    return permission.includes(action);
  }, [permission]);

const detectPlatform = useCallback(() => {
  const userAgent = navigator.userAgent.toLowerCase();
  const isAndroid = /android/.test(userAgent);
  const isIOS = /iphone|ipad|ipod/.test(userAgent) || 
                (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  
  return { isAndroid, isIOS, isMobile: isAndroid || isIOS };
}, []);

const platform = useMemo(() => detectPlatform(), [detectPlatform]);

const parseDateTimeForMobile = useCallback((sessionDate, time) => {
  try {
    const dateMatch = sessionDate.match(/(\d{4})-(\d{2})-(\d{2})|(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    let year, month, day;
    
    if (dateMatch) {
      if (dateMatch[1]) { 
        year = parseInt(dateMatch[1]);
        month = parseInt(dateMatch[2]) - 1; 
        day = parseInt(dateMatch[3]);
      } else if (dateMatch[4]) { 
        month = parseInt(dateMatch[4]) - 1; 
        day = parseInt(dateMatch[5]);
        year = parseInt(dateMatch[6]);
      }
    }
    
    const timeMatch = time.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
    let hours = 0, minutes = 0, seconds = 0;
    
    if (timeMatch) {
      hours = parseInt(timeMatch[1]);
      minutes = parseInt(timeMatch[2]);
      seconds = timeMatch[3] ? parseInt(timeMatch[3]) : 0;
    }
    
    return new Date(year, month, day, hours, minutes, seconds);
  } catch (error) {
    console.error('Error parsing date/time for mobile:', error);
    return new Date();
  }
}, []);

// get tech session calendar events data
const getSessionCalendarData = useCallback(async (date = new Date()) => {
  setIsLoading(true);
  setError(null);
  
  try {
    const month = date.getMonth() + 1;
    const year = date.getFullYear();

    const response = await UseAxios(getTechSessionData(month, year));
    const data = response?.data?.data || {};
    
    const formattedData = response.data?.data?.techSessionDetails
      ?.filter(detail => detail.isApproved === 'Approved' || detail.isApproved === 'Completed')
      ?.map(detail => {
        let startDate, endDate;
        
        if (platform.isMobile) {
          startDate = parseDateTimeForMobile(detail.sessionDate, detail.startTime);
          endDate = parseDateTimeForMobile(detail.sessionDate, detail.endTime);
        } else {
          startDate = new Date(`${detail.sessionDate} ${detail.startTime}`);
          endDate = new Date(`${detail.sessionDate} ${detail.endTime}`);
        }
        
        return {
          ...detail,
          title: detail.sessionName,
          start: startDate,
          end: endDate,
        };
      }) || [];

    setTechSessionMetrics(data);
    setTechSessionData(formattedData);
    setCurrentViewDate(date);
  } catch (error) {
    setTechSessionData([]);
    setError('Failed to load tech session data');
    console.error('Error fetching tech session calendar data:', error);
  } finally {
    setIsLoading(false);
  }
}, [platform.isMobile, parseDateTimeForMobile]);


  useEffect(() => {
    getSessionCalendarData();
  }, [getSessionCalendarData]);

  // Memoize grouped sessions calculation
  const groupedSessions = useMemo(() => {
    return Array.isArray(techSessionData) ? techSessionData.reduce((acc, session) => {
      const dateKey = session.start.toDateString();
      if (!acc[dateKey]) acc[dateKey] = [];
      acc[dateKey].push(session);
      return acc;
    }, {}) : {};
  }, [techSessionData]);

  // Memoize grouped event list
  const groupedEventList = useMemo(() => {
    return Object.entries(groupedSessions).map(([__, events]) => {
      const first = events[0];
      return {
        ...first,
        groupedSessions: events, // pass all events for tooltip
      };
    });
  }, [groupedSessions]);

  // Memoize event dates set
  const eventDatesSet = useMemo(() => {
    return new Set(
      techSessionData?.map((event) => new Date(event.start).toDateString()),
    );
  }, [techSessionData]);

  // Memoize navigation button click handler
  const handleSignupNavigation = useCallback(() => {
    navigate('/tech-session/signup');
  }, [navigate]);

  // Memoize modal close handler
  const handleModalClose = useCallback(() => {
    setOpen(false);
  }, []);

  // Memoize day prop getter with better performance for large datasets
  const dayPropGetter = useCallback((date) => {
    const dateKey = date.toDateString();
    if (eventDatesSet.has(dateKey)) {
      return { 
        style: { 
          backgroundColor: '#E1DFF2',
          // Add will-change for better performance during animations
          willChange: 'background-color' 
        } 
      };
    }
    return {};
  }, [eventDatesSet]);

  // Memoize formats object with optimized date formatting
  const calendarFormats = useMemo(() => ({
    dateFormat: (date) => {
      // Use more efficient date formatting for better performance
      return date.getDate().toString();
    },
    dayFormat: 'ddd',
    weekdayFormat: 'ddd',
    monthHeaderFormat: 'MMMM YYYY',
  }), []);

  const handleNavigate = useCallback((date) => {
  setCurrentViewDate(date);
  getSessionCalendarData(date);
}, [getSessionCalendarData]);

  // Memoize calendar props to prevent unnecessary re-renders
  const calendarProps = useMemo(() => ({
    events: groupedEventList,
    dayPropGetter,
    formats: calendarFormats,
    popup: true,
    popupOffset: 30,
    showMultiDayTimes: true,
    step: 60,
    timeslots: 1,
    // Reduce re-renders
    titleAccessor: 'title',
    startAccessor: 'start',
    endAccessor: 'end',
  }), [groupedEventList, dayPropGetter, calendarFormats, handleNavigate]);

  return (
  <div className="tech-sessions-container">
    <div className="tech-session-header">
      <div className="tech-session-title">
        {TECH_SESSION_PAGE.CALENDER_HEADER}
      </div>

      {isLoading ? (
          <div className="tech-session-loader">
            <img src={puzzleLoader} alt="Loading..." className="loader-gif" />
          </div>
        ) : (
        <div className="tech-sessions-calender-container">
          <div className="tech-session-top-section">
            <MetricsCards
              techSessionData={techSessionMetrics}
              hasAccess={hasAccess}
              btnOnclick={handleSignupNavigation}
              selectedDate={currentViewDate}
            />
          </div>
          <div className="tech-session-calendar-container">
            <CommonCalendar
              {...calendarProps}
              date={currentViewDate}
              onNavigate={handleNavigate}
            />
          </div>
          <Suspense fallback={<div>Loading...</div>}>
            <AddEditTechSession 
              open={open} 
              onClose={handleModalClose} 
            />
          </Suspense>
        </div>
      )}
    </div>
  </div>
);
};

export default TechSessionCalendarView;